import { RegisteredStarComponent } from '../../components/registered-star/registered-star.component';
import { Component, inject, signal, computed, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RaceService } from '../../services/race.service';
import { DashboardRaceWinner, ResultService } from '../../services/result.service';
import { RouterModule } from '@angular/router';
import { TranslationService } from '../../services/translation.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { DisplayValuePipe } from '../../pipes/display-value.pipe';
import { DistancePipe } from '../../pipes/distance.pipe';
import { Event } from '../../models/data.models';
import { BrandedLoaderComponent } from '../../components/branded-loader/branded-loader.component';

/** Races whose winner is highlighted on each event card, largest fields first. */
const WINNERS_PER_EVENT = 4;

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [RegisteredStarComponent, CommonModule, RouterModule, TranslatePipe, DisplayValuePipe, DistancePipe, BrandedLoaderComponent],
  templateUrl: './dashboard.component.html'
})
export class DashboardComponent implements OnDestroy {
  private raceService = inject(RaceService);
  private resultService = inject(ResultService);
  translationService = inject(TranslationService);

  races = signal<Event[]>([]);
  winnersMap = signal<Map<string, DashboardRaceWinner[]>>(new Map());
  participantCounts = signal<Map<string, number>>(new Map());
  isLoading = signal(true);
  countdown = signal<{ days: number, hours: number, minutes: number }>({ days: 0, hours: 0, minutes: 0 });

  /** Events that already took place, most recent first: the activity feed. */
  pastEvents = computed(() => this.races()
    .filter(event => !this.isUpcoming(event))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));

  totalRaces = computed(() => this.races().length);
  totalDistance = computed(() => this.races()
    .flatMap(event => event.races ?? [])
    .reduce((total, race) => total + (race.distanceKm || 0), 0));
  totalParticipations = computed(() => [...this.participantCounts().values()]
    .reduce((total, count) => total + count, 0));
  totalRaceCount = computed(() => this.pastEvents()
    .reduce((total, event) => total + (event.races?.length ?? 0), 0));

  nextRace = computed(() => this.races()
    .filter(event => this.isUpcoming(event))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0] ?? null);

  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    void this.loadDashboardData();
    // Minutes are the finest unit displayed: refreshing every 30 s is enough.
    this.countdownInterval = setInterval(() => this.updateCountdown(), 30_000);
  }

  ngOnDestroy() {
    if (this.countdownInterval) clearInterval(this.countdownInterval);
  }

  private updateCountdown(): void {
    const next = this.nextRace();
    if (!next) return;
    const diff = new Date(next.date).getTime() - Date.now();
    if (diff <= 0) return;
    this.countdown.set({
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    });
  }

  async loadDashboardData() {
    this.isLoading.set(true);
    try {
      const { data: events } = await this.raceService.getEvents(0, 50);
      this.races.set(events);
      this.updateCountdown();

      const { winners, participantCounts } = await this.resultService.getDashboardRaceWinners(
        events.filter(event => !this.isUpcoming(event)).map(event => event.id),
      );
      this.winnersMap.set(winners);
      this.participantCounts.set(participantCounts);
    } catch (err) {
      console.error('Error loading dashboard:', err);
    } finally {
      this.isLoading.set(false);
    }
  }

  /** An event is upcoming from its calendar day onwards, whatever its stored status. */
  isUpcoming(event: Event): boolean {
    const date = new Date(event.date);
    if (Number.isNaN(date.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.getTime() >= today.getTime();
  }

  /** Winners of the largest races of an event; races nobody finished have no winner. */
  getWinners(eventId: string): DashboardRaceWinner[] {
    return (this.winnersMap().get(eventId) ?? [])
      .filter(winner => (winner.timeSeconds ?? 0) > 0 && !!winner.humanName)
      .slice(0, WINNERS_PER_EVENT);
  }

  distanceRange(event: Event): { min: number; max: number } | null {
    const distances = (event.races ?? []).map(race => race.distanceKm).filter(distance => distance > 0);
    if (distances.length === 0) return null;
    return { min: Math.min(...distances), max: Math.max(...distances) };
  }

  disciplineCount(event: Event): number {
    return new Set((event.races ?? []).map(race => race.discipline).filter(Boolean)).size;
  }

  formatDate(isoString: string): string {
    const locale = this.translationService.currentLang() === 'fr' ? 'fr-FR' : 'en-US';
    return new Date(isoString).toLocaleDateString(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  }

  formatTime(totalSeconds: number | null): string {
    if (!totalSeconds || totalSeconds <= 0) return '—';
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const pad = (num: number) => num.toString().padStart(2, '0');
    if (hours > 0) {
      return `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
    }
    return `${pad(minutes)}m ${pad(seconds)}s`;
  }
}
